/**
 * The portfolio registers: Projects & Contracts, and Parties & Contractors, plus the
 * front door's create affordances (M5 P4) and the detail pages.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The registers are read models; the writes go through the action-dialog pattern (M5
 * P5e #8): one overlay per action, a timed toast for the outcome, and a refusal rendered
 * in place off the problem document the server sent.
 *
 * M6 Station 2b (amendment 1): there is no award in Qualify. A project is registered
 * WITH its already-awarded contractor (FrontDoorDialogs), approved by somebody else,
 * then staffed. The detail pages act through the next-action rail the server computes.
 */

import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router";

import type { TaskRecord } from "../api/tasks";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import {
  getContract,
  getProject,
  getTaxonomies,
  listContracts,
  listContractorDossiers,
  listProjects,
  type ContractSummary,
  type ContractorDossier,
  type NextActionOut,
  type ProjectSummary,
} from "../api/portfolio";
import type { Me } from "../api/me";
import { HistoryButton } from "../components/HistoryDrawer";
import { NextActionRail } from "../components/NextActionRail";
import {
  AttachDocumentsDialog,
  DocumentsBlock,
  TeamBlock,
  type DocumentTarget,
} from "./ProjectBlocks";
import { PortalAccountsBlock } from "./PortalAccounts";
import { RecordTasksBlock, noRecordTasksReason, useRecordTasks } from "./RecordTasks";
import { ToastBar } from "./ActionDialog";
import { ActionButton } from "@/components/ui/action-button";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { Toast } from "@/components/ui/toast";
import {
  ApprovalDialog,
  AssignDialog,
  CreateProjectDialog,
  ProjectHistoryBlock,
  ProjectStatusChip,
  projectStatusLabel,
} from "./FrontDoorDialogs";
import {
  AbsencePanel,
  EmptyState,
  LoadingLine,
  StateChip,
  day,
  Facts,
  Section,
  Breadcrumbs,
  type Crumb,
} from "../screens/parts";
import { useQuery } from "../screens/useQuery";
import { mayActAs, useViewerSide } from "../lib/actor";
import { CriticalityRatingsBlock } from "./CriticalityRatings";
import { StaffingPanel } from "./StaffingPanel";
import { listCalendarActivities, type CalendarActivity } from "../api/calendar";
import { listFindings, type FindingSummary } from "../api/findings";
import { listItps, type ItpSummary } from "../api/itps";
import { listRfis } from "../api/rfis";
import { listSchedules, listCalendarPeriods, type Activity } from "../api/schedules";
import { listVerifications, type VerificationSummary } from "../api/verifications";
import { term } from "../brand/strings";
import { inspectionRegisterStateLabel } from "../screens/Inspections";
import { YearGrid } from "../screens/YearGrid";
import { BarChart, type BarDatum } from "../charts/BarChart";
import { DonutChart, type DonutDatum } from "../charts/DonutChart";
import { DivergingBars, type DivergingRow } from "../charts/DivergingBars";

/** The signed-in persona, when the page renders inside the shell's outlet. */
function useMe(): Me | null {
  const outlet = useOutletContext<{ me: Me } | null>();
  return outlet?.me ?? null;
}

/**
 * The create affordances are the owner side's; the server refuses every wrong write.
 * Wave W1: the third party inspector (ruling 6) and the read-only management class
 * (ruling 5) see none either.
 */
function mayManage(me: Me | null): boolean {
  const workflowClass = me?.workflowClass;
  return (
    workflowClass !== "contractor" && workflowClass !== "tpi" && workflowClass !== "management"
  );
}

/** The breadcrumbs pattern (M5 P4), on the house `q-crumbs` component (M5 P6 seat V). */
function Breadcrumb({ trail }: { readonly trail: readonly (string | null)[] }): React.JSX.Element {
  // Home first (M6 Station 2b): a detail page opened from the landing always has its
  // way back, whatever the persona's sidebar carries.
  const crumbs: Crumb[] = [{ label: strings.nav.home, to: "/" }];
  const [root, ...rest] = trail;
  if (typeof root === "string") {
    crumbs.push({ label: root, to: "/portfolio/projects" });
  }
  for (const label of rest) {
    if (typeof label === "string") {
      crumbs.push({ label });
    }
  }
  return <Breadcrumbs trail={crumbs} />;
}

/**
 * Wave W2 (Director's finding 2026-10-10): a contractor registered with nobody who can
 * sign in. Its tasks (the ITP request first) reach nobody until its people exist; the
 * page says so plainly. The onboarding module that invites them is a later module.
 */
function NoPortalUsersNotice({ party }: { readonly party: string | null }): React.JSX.Element {
  return (
    <div className="q-banner" role="note" data-testid="no-portal-users">
      <p className="q-thread__body">
        {party === null
          ? strings.portfolio.noPortalUsers
          : strings.portfolio.noPortalUsersNamed(party)}
      </p>
    </div>
  );
}

/** Scroll to the record's own tasks block (the rail's "View open tasks"). */
function showRecordTasks(): void {
  document.getElementById("record-tasks")?.scrollIntoView();
}

/** "start to end", or whichever end is known, or nothing: never a dangling "to". */
function dateRange(start: string | null, end: string | null): string {
  if (start === null && end === null) {
    return "";
  }
  return `${day(start) || "not set"} to ${day(end) || "not set"}`;
}

/* ---------------------------------------------------------------------- */
/* Projects & Contracts.                                                   */
/* ---------------------------------------------------------------------- */

export function ProjectsPage(): React.JSX.Element {
  const token = useToken();
  const me = useMe();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stamp, setStamp] = useState(0);
  const [dialog, setDialog] = useState<"project" | "attach" | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: "info" | "danger" } | null>(null);
  const projects = useQuery(() => listProjects(token), [token, stamp]);
  // The register lists MAIN contracts only (wave W0): sub-contracts are an FYI on their
  // main contract's page. The free Create-contract dialog is retired.
  const contracts = useQuery(() => listContracts(token, { mainOnly: true }), [token, stamp]);
  // Re-read the register after a create lands, so the new row is on screen immediately.
  const reload = (): void => {
    setStamp((one) => one + 1);
  };

  const act = searchParams.get("act");
  const shownDialog =
    dialog ?? (act === "register" ? "project" : act === "attach" ? "attach" : null);

  const attachTarget = useMemo<DocumentTarget | null>(() => {
    if (contracts.kind === "ready" && contracts.data.items.length > 0) {
      const c = contracts.data.items[0];
      if (c) {
        return { entityType: "CONTRACT", entityId: c.id, label: c.humanId };
      }
    }
    if (projects.kind === "ready" && projects.data.items.length > 0) {
      const p = projects.data.items[0];
      if (p) {
        return { entityType: "PROJECT", entityId: p.id, label: p.humanId };
      }
    }
    return null;
  }, [contracts, projects]);

  const projectColumns = useMemo(() => projectRegisterColumns(), []);
  const contractColumns = useMemo(
    () => contractRegisterColumns(projects.kind === "ready" ? projects.data.items : []),
    [projects],
  );

  return (
    <>
      {toast !== null && (
        <Toast
          message={toast.message}
          tone={toast.tone}
          onDone={() => {
            setToast(null);
          }}
        />
      )}
      <header className="q-ui mb-(--q-s1) flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1 basis-[28rem]">
          <h2 className="q-shell__heading">{strings.portfolio.projectsHeading}</h2>
          <p className="q-shell__lede">{strings.portfolio.projectsLede}</p>
        </div>
        {/* Every action this page offers, in one row at the top (Mahmoud's action rule). */}
        {/* Wave W1.5: offered only when the server says this account may register a
            project (REGISTER_PROJECT); before, every operator persona saw it and most
            were refused at submit. */}
        {mayManage(me) &&
          projects.kind === "ready" &&
          projects.data.startCodes.includes("REGISTER_PROJECT") && (
            <div
              className="flex flex-wrap items-center gap-3 sm:justify-end"
              data-testid="portfolio-create-bar"
            >
              <ActionButton
                data-testid="create-project-button"
                onClick={() => {
                  setDialog("project");
                }}
              >
                <Plus aria-hidden="true" />
                {strings.portfolio.createProject}
              </ActionButton>
            </div>
          )}
      </header>

      <RegisterCard title={strings.portfolio.projectsRegisterTitle}>
        {projects.kind === "loading" && <LoadingLine label={strings.common.loading} />}
        {projects.kind === "absence" && <AbsencePanel />}
        {projects.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
        {projects.kind === "ready" &&
          (projects.data.items.length === 0 ? (
            <EmptyState
              title={strings.portfolio.projectsEmptyTitle}
              body={strings.portfolio.projectsEmptyBody}
            />
          ) : (
            <DataTable
              data={projects.data.items}
              columns={projectColumns}
              caption={strings.portfolio.projectsHeading}
              testId="projects"
              getRowId={(row) => row.id}
            />
          ))}
      </RegisterCard>

      <RegisterCard title={strings.portfolio.contractsHeading}>
        {contracts.kind === "loading" && <LoadingLine label={strings.common.loading} />}
        {contracts.kind === "absence" && <AbsencePanel />}
        {contracts.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
        {contracts.kind === "ready" &&
          (contracts.data.items.length === 0 ? (
            <EmptyState
              title={strings.portfolio.contractsEmptyTitle}
              body={strings.portfolio.contractsEmptyBody}
            />
          ) : (
            <DataTable
              data={contracts.data.items}
              columns={contractColumns}
              caption={strings.portfolio.contractsHeading}
              testId="contracts"
              getRowId={(row) => row.id}
            />
          ))}
      </RegisterCard>

      {shownDialog === "project" && (
        <CreateProjectDialog
          token={token}
          projects={projects.kind === "ready" ? projects.data.items : []}
          mayRegisterContractor={
            projects.kind === "ready" && projects.data.startCodes.includes("REGISTER_PARTY")
          }
          onClose={() => {
            setDialog(null);
            if (act === "register") {
              const next = new URLSearchParams(searchParams);
              next.delete("act");
              setSearchParams(next, { replace: true });
            }
          }}
          onCreated={({ created, contractorName, attachFailures, accountAssignFailed }) => {
            setDialog(null);
            if (act === "register") {
              const next = new URLSearchParams(searchParams);
              next.delete("act");
              setSearchParams(next, { replace: true });
            }
            reload();
            const registered = strings.frontDoor.registered(created.humanId, contractorName);
            const notes = [
              attachFailures > 0 ? strings.frontDoor.attachFailed(attachFailures) : null,
              accountAssignFailed === undefined
                ? null
                : strings.portalAccounts.assignFailed(accountAssignFailed),
            ].filter((one) => one !== null);
            setToast(
              notes.length > 0
                ? { message: [registered, ...notes].join(" "), tone: "danger" }
                : { message: registered, tone: "info" },
            );
          }}
        />
      )}

      {shownDialog === "attach" && attachTarget !== null && (
        <AttachDocumentsDialog
          token={token}
          target={attachTarget}
          onClose={() => {
            setDialog(null);
            if (act === "attach") {
              const next = new URLSearchParams(searchParams);
              next.delete("act");
              setSearchParams(next, { replace: true });
            }
            reload();
          }}
          onAttached={(count) => {
            setDialog(null);
            if (act === "attach") {
              const next = new URLSearchParams(searchParams);
              next.delete("act");
              setSearchParams(next, { replace: true });
            }
            reload();
            setToast({
              message: strings.frontDoor.attachedToast(count, attachTarget.label),
              tone: "info",
            });
          }}
        />
      )}
    </>
  );
}

/** A register card: its title, then its states or its DataTable (UX_SPEC 2.1). */
function RegisterCard({
  title,
  children,
}: {
  readonly title?: string | undefined;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="q-ui mb-(--q-s2) flex min-w-0 flex-col gap-3">
      {title !== undefined && (
        <h3 className="text-xs tracking-[0.16em] text-muted-foreground uppercase">{title}</h3>
      )}
      {children}
    </section>
  );
}

/** The project register's columns: what each cell says, in plain words. */
function projectRegisterColumns(): RegisterColumn<ProjectSummary>[] {
  const copy = strings.portfolio;
  return [
    {
      id: "project",
      label: copy.columnProject,
      text: (row) => `${row.humanId} ${row.name}`,
      sortValue: (row) => row.humanId,
      className: "min-w-[14rem]",
      render: (row) => (
        <>
          <Link
            to={`/portfolio/projects/${row.id}`}
            className="tracking-[0.03em] whitespace-nowrap text-gold no-underline hover:underline"
          >
            {row.humanId}
          </Link>
          <div className="max-w-[34rem] [overflow-wrap:anywhere] text-word">{row.name}</div>
        </>
      ),
    },
    { id: "category", label: copy.columnCategory, text: (row) => row.category ?? "", filter: true },
    { id: "location", label: copy.columnLocation, text: (row) => row.location ?? "", filter: true },
    {
      id: "lead",
      label: copy.columnLead,
      text: (row) => row.leadQaqcDisplayName ?? "",
      filter: true,
    },
    {
      id: "status",
      label: strings.frontDoor.statusColumn,
      text: (row) => projectStatusLabel(row.status),
      filter: true,
      render: (row) => <ProjectStatusChip status={row.status} />,
    },
    {
      id: "dates",
      label: copy.columnDates,
      text: (row) => dateRange(row.startDate, row.endDate ?? row.completionDate),
      sortValue: (row) => row.startDate ?? "",
      className: "min-w-[8rem]",
    },
  ];
}

/** The contract register's columns; the project cell reads the projects already loaded. */
function contractRegisterColumns(
  projects: readonly ProjectSummary[],
): RegisterColumn<ContractSummary>[] {
  const copy = strings.portfolio;
  const projectOf = (row: ContractSummary): string =>
    row.linkedProjectIds
      .map((id) => projects.find((one) => one.id === id))
      .flatMap((one) => (one === undefined ? [] : [`${one.humanId} · ${one.name}`]))
      .join(", ");
  return [
    {
      id: "contract",
      label: copy.columnContract,
      text: (row) =>
        row.parentContractId !== null ? `${row.humanId} ${copy.subContract}` : row.humanId,
      render: (row) => (
        <>
          <Link
            to={`/portfolio/contracts/${row.id}`}
            className="tracking-[0.03em] whitespace-nowrap text-gold no-underline hover:underline"
          >
            {row.humanId}
          </Link>
          {row.parentContractId !== null && (
            <div className="text-muted-foreground">{copy.subContract}</div>
          )}
        </>
      ),
    },
    {
      id: "project",
      label: copy.columnProject,
      text: projectOf,
      className: "min-w-[14rem] [overflow-wrap:anywhere]",
    },
    {
      id: "contractor",
      label: copy.columnContractor,
      text: (row) => row.contractorPartyName,
      filter: true,
    },
    {
      id: "status",
      label: copy.columnStatus,
      text: (row) => row.statusLabel,
      filter: true,
      render: (row) => <StateChip state={row.status} label={row.statusLabel} />,
    },
    {
      id: "dates",
      label: copy.columnDates,
      text: (row) => dateRange(row.startDate, row.endDate),
      sortValue: (row) => row.startDate ?? "",
      className: "whitespace-nowrap",
    },
  ];
}

/* ---------------------------------------------------------------------- */
/* The detail views: the project with its approval, staffing and history, */
/* and the contract. Every act goes through the server's next actions.    */
/* ---------------------------------------------------------------------- */

function asScheduleActivity(activity: CalendarActivity): Activity {
  return {
    id: activity.id,
    humanId: activity.humanId,
    scheduleId: activity.scheduleId,
    scheduleWorkflowState: "",
    plannedDate: activity.plannedDate,
    plannedEndDate: activity.plannedEndDate,
    status: activity.status,
    isTerminal: false,
    priority: activity.priority,
    purpose: activity.purpose,
    checklistReference: null,
    contractorPartyId: activity.contractorPartyId,
    contractorPartyName: activity.contractorPartyName,
    contractId: null,
    verificationTypeCode: activity.verificationTypeCode,
    verificationTypeLabel: activity.verificationTypeLabel,
    assessorPersonIds: [],
    assessorDisplayNames: [...activity.assessorDisplayNames],
    negotiationState: activity.negotiationState,
    pendingDate: activity.pendingDate,
    availableMoves: [],
    availableEvents: [],
    inspectionId: null,
    mayConductInspection: false,
    actionableByMe: activity.actionableByMe === true,
  };
}

function projectItpColumns(): RegisterColumn<ItpSummary>[] {
  return [
    {
      id: "itp",
      label: strings.itps.columnPlan,
      text: (row) => `${row.humanId} ${row.title}`,
      sortValue: (row) => row.humanId,
      render: (row) => (
        <>
          <Link
            to={`/itps/${row.id}`}
            className="tracking-[0.03em] whitespace-nowrap text-gold no-underline hover:underline"
          >
            {row.humanId}
          </Link>
          <div className="max-w-[28rem] [overflow-wrap:anywhere] text-word">{row.title}</div>
        </>
      ),
    },
    {
      id: "contractor",
      label: strings.itps.columnContractor,
      text: (row) => row.contractorPartyName,
      filter: true,
    },
    {
      id: "discipline",
      label: strings.itps.columnDiscipline,
      text: (row) => row.disciplineLabel,
      filter: true,
    },
    {
      id: "items",
      label: strings.itps.columnItems,
      text: (row) => String(row.itemCount),
      sortValue: (row) => row.itemCount,
      className: "whitespace-nowrap",
    },
    {
      id: "state",
      label: strings.itps.columnState,
      text: (row) => term(row.workflowState),
      filter: true,
      render: (row) => <StateChip state={row.workflowState} />,
    },
  ];
}

function projectVerificationColumns(): RegisterColumn<VerificationSummary>[] {
  return [
    {
      id: "verification",
      label: strings.inspections.columnRecord,
      text: (row) => `${row.humanId} ${row.title}`,
      sortValue: (row) => row.humanId,
      render: (row) => (
        <>
          <Link
            to={`/inspections/${row.id}`}
            className="tracking-[0.03em] whitespace-nowrap text-gold no-underline hover:underline"
          >
            {row.humanId}
          </Link>
          <div className="max-w-[28rem] [overflow-wrap:anywhere] text-word">{row.title}</div>
        </>
      ),
    },
    {
      id: "date",
      label: strings.inspections.columnDate,
      text: (row) => day(row.plannedDate),
      sortValue: (row) => row.plannedDate,
      className: "whitespace-nowrap",
    },
    {
      id: "contractor",
      label: strings.inspections.columnContractor,
      text: (row) => row.contractorPartyName,
      filter: true,
    },
    {
      id: "state",
      label: strings.inspections.columnState,
      text: (row) => inspectionRegisterStateLabel(row),
      filter: true,
      render: (row) => (
        <StateChip state={row.workflowState} label={inspectionRegisterStateLabel(row)} />
      ),
    },
    {
      id: "outcome",
      label: strings.inspections.columnOutcome,
      text: (row) => (row.outcome !== null ? term(row.outcome) : ""),
      filter: true,
      render: (row) => (row.outcome !== null ? <StateChip state={row.outcome} /> : null),
    },
  ];
}

function projectFindingColumns(): RegisterColumn<FindingSummary>[] {
  return [
    {
      id: "finding",
      label: strings.findings.columnFinding,
      text: (row) => `${row.humanId} ${row.description}`,
      sortValue: (row) => row.humanId,
      render: (row) => (
        <>
          <Link
            to={`/findings/${row.id}`}
            className="tracking-[0.03em] whitespace-nowrap text-gold no-underline hover:underline"
          >
            {row.humanId}
          </Link>
          <div className="max-w-[28rem] [overflow-wrap:anywhere] text-word">{row.description}</div>
        </>
      ),
    },
    {
      id: "class",
      label: strings.findings.columnClass,
      text: (row) => term(row.findingClass),
      filter: true,
    },
    {
      id: "observedOn",
      label: strings.findings.observedOnLabel,
      text: (row) => day(row.observedAt),
      sortValue: (row) => row.observedAt,
      className: "whitespace-nowrap",
    },
    {
      id: "daysOpen",
      label: strings.findings.columnDaysOpen,
      text: (row) => (row.daysOpen !== null ? String(row.daysOpen) : ""),
      sortValue: (row) => row.daysOpen ?? -1,
      className: "whitespace-nowrap",
    },
    {
      id: "state",
      label: strings.findings.columnState,
      text: (row) => term(row.findingStatus),
      filter: true,
      render: (row) => <StateChip state={row.findingStatus} />,
    },
  ];
}

function projectPunchColumns(): RegisterColumn<FindingSummary>[] {
  return [
    {
      id: "punch",
      label: strings.findings.columnFinding,
      text: (row) => `${row.humanId} ${row.description}`,
      sortValue: (row) => row.humanId,
      render: (row) => (
        <>
          <Link
            to={`/findings/${row.id}`}
            className="tracking-[0.03em] whitespace-nowrap text-gold no-underline hover:underline"
          >
            {row.humanId}
          </Link>
          <div className="max-w-[28rem] [overflow-wrap:anywhere] text-word">{row.description}</div>
        </>
      ),
    },
    {
      id: "punchList",
      label: strings.findings.punchListLabel,
      text: (row) => row.punchListName ?? "",
      filter: true,
    },
    {
      id: "targetDate",
      label: strings.findings.proposedTargetDate,
      text: (row) => (row.targetCompletionDate ? day(row.targetCompletionDate) : ""),
      sortValue: (row) => row.targetCompletionDate ?? "",
      className: "whitespace-nowrap",
    },
    {
      id: "state",
      label: strings.findings.columnState,
      text: (row) => term(row.findingStatus),
      filter: true,
      render: (row) => <StateChip state={row.findingStatus} />,
    },
  ];
}

function ProjectContractTree({
  contracts,
  projectId,
  mayRevoke,
  mayAssign,
}: {
  readonly contracts: readonly ContractSummary[];
  readonly projectId: string;
  /** The project detail's `can_revoke_portal_contracts`: the server's word. */
  readonly mayRevoke: boolean;
  /** The project detail's `can_assign_portal_contracts` (Wave Experience A). */
  readonly mayAssign: boolean;
}): React.JSX.Element {
  const copy = strings.portfolio;
  if (contracts.length === 0) {
    return <p className="q-note">{copy.mainContractNone}</p>;
  }

  const topLevel = contracts.filter(
    (c) => c.parentContractId === null || !contracts.some((p) => p.id === c.parentContractId),
  );
  const mainContract = topLevel.find((c) => c.isMain) ?? topLevel[0];
  if (mainContract === undefined) {
    return <p className="q-note">{copy.mainContractNone}</p>;
  }
  const tpiContracts = topLevel.filter((c) => c.id !== mainContract.id);

  const renderNode = (
    c: ContractSummary,
    roleLabel: string,
    testId?: string,
  ): React.JSX.Element => {
    const children = contracts.filter((child) => child.parentContractId === c.id);
    return (
      <div
        key={c.id}
        className="q-contract-node space-y-2 border-b border-border/40 pb-4 last:border-b-0"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">
            {roleLabel}
          </span>
        </div>
        {c.contractorPortalUsers === 0 && <NoPortalUsersNotice party={c.contractorPartyName} />}
        <Facts
          entries={[
            [
              copy.columnContract,
              <Link
                key="link"
                to={`/portfolio/contracts/${c.id}`}
                className="q-register__mono"
                data-testid={testId}
              >
                {c.humanId}
              </Link>,
            ],
            [copy.columnContractor, c.contractorPartyName],
            [copy.columnStatus, <StateChip key="state" state={c.status} label={c.statusLabel} />],
            [copy.columnDates, dateRange(c.startDate, c.endDate)],
          ]}
        />
        {children.length > 0 && (
          <div
            className="ml-6 pl-4 border-l-2 border-border/60 mt-3 space-y-3"
            data-testid={`contract-children-${c.id}`}
          >
            {children.map((child) => renderNode(child, copy.subContract, "project-sub-contract"))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="project-contract-tree">
      {renderNode(mainContract, copy.mainContractHeading, "project-main-contract")}
      {tpiContracts.map((tpi) => renderNode(tpi, copy.tpiContract, "project-tpi-contract"))}
      {[...new Map(contracts.map((c) => [c.contractorPartyId, c.contractorPartyName]))].map(
        ([partyId, partyName]) => (
          <PortalAccountsBlock
            key={partyId}
            partyId={partyId}
            partyName={partyName}
            projectId={projectId}
            mayRevoke={mayRevoke}
            mayAssign={mayAssign}
          />
        ),
      )}
    </div>
  );
}

const FINDING_SEVERITY_CLASSES = [
  "NEGATIVE_OBSERVATION",
  "COMMENT",
  "OFI",
  "POSITIVE_OBSERVATION",
] as const;

type ProjectOverlay = "approve" | "assign" | "attach" | null;

export function ProjectDetailPage(): React.JSX.Element {
  const token = useToken();
  const navigate = useNavigate();
  const { projectId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stamp, setStamp] = useState(0);
  const projectDetail = useQuery(
    (signal) => getProject(token, projectId, signal),
    [token, projectId, stamp],
  );
  const projects = useQuery(() => listProjects(token), [token]);
  const taxonomies = useQuery(() => getTaxonomies(token), [token]);
  const allContracts = useQuery(
    (signal) => listContracts(token, { projectId, signal }),
    [token, projectId, stamp],
  );
  const schedules = useQuery(
    (signal) => listSchedules(token, { projectId, signal }),
    [token, projectId, stamp],
  );
  const [calendarYear] = useState(() => new Date().getFullYear());
  const [showShutdown, setShowShutdown] = useState(true);
  const [showRamadan, setShowRamadan] = useState(true);
  const calendarActivities = useQuery(
    (signal) =>
      listCalendarActivities(token, {
        fromDate: `${String(calendarYear)}-01-01`,
        toDate: `${String(calendarYear)}-12-31`,
        limit: 500,
        signal,
      }),
    [token, calendarYear, stamp],
  );
  const calendarPeriods = useQuery((signal) => listCalendarPeriods(token, signal), [token]);
  const rfis = useQuery((signal) => listRfis(token, { signal }), [token, stamp]);
  const itps = useQuery((signal) => listItps(token, { signal }), [token, stamp]);
  const verifications = useQuery((signal) => listVerifications(token, { signal }), [token, stamp]);
  const findings = useQuery(
    (signal) => listFindings(token, { projectId, signal }),
    [token, projectId, stamp],
  );

  const [overlay, setOverlay] = useState<ProjectOverlay>(null);
  const [toast, setToast] = useState<{ message: string; tone: "info" | "danger" } | null>(null);
  const [registerTab, setRegisterTab] = useState<"itps" | "verifications" | "findings" | "punches">(
    "itps",
  );

  // Wave W2: this project's open tasks, inline; "View open tasks" means these.
  const taskRecord = useMemo<TaskRecord>(() => ({ type: "PROJECT", id: projectId }), [projectId]);
  const recordTasks = useRecordTasks(token, taskRecord, stamp);

  const detail = projectDetail.kind === "ready" ? projectDetail.data : null;
  const project =
    detail ??
    (projects.kind === "ready"
      ? (projects.data.items.find((one) => one.id === projectId) ?? null)
      : null);

  // The server's next actions are the only source of what this caller may do here. When
  // the detail read fails, the task list is the one place left to point at.
  const actions: readonly NextActionOut[] =
    detail !== null
      ? detail.nextActions
      : project !== null
        ? [{ code: "OPEN_TASK_LIST", method: "GET", href: "/api/tasks" }]
        : [];
  const offered = (code: string): boolean => actions.some((one) => one.code === code);

  // A task link carries ?act=approve or ?act=assign: the overlay opens once the server
  // has said the act is still this caller's; otherwise the page says plainly it is gone.
  const act = searchParams.get("act");
  const actCode =
    act === "approve"
      ? "APPROVE_PROJECT"
      : act === "assign"
        ? "ASSIGN_QC_ENGINEER"
        : act === "attach"
          ? "ATTACH_DOCUMENT"
          : null;
  const actOpen =
    detail !== null && actCode !== null && detail.nextActions.some((one) => one.code === actCode);
  const actGone = act !== null && detail !== null && !actOpen;
  const shown: ProjectOverlay =
    overlay ??
    (actOpen ? (act === "approve" ? "approve" : act === "assign" ? "assign" : "attach") : null);
  const closeOverlay = (): void => {
    setOverlay(null);
    if (act !== null) {
      const next = new URLSearchParams(searchParams);
      next.delete("act");
      setSearchParams(next, { replace: true });
    }
  };

  const reread = (): void => {
    setStamp((one) => one + 1);
  };

  const projectContractIds = useMemo(() => {
    if (allContracts.kind !== "ready") {
      return new Set<string>();
    }
    return new Set(allContracts.data.items.map((c) => c.id));
  }, [allContracts]);

  const projectScheduleIds = useMemo(() => {
    if (schedules.kind !== "ready") {
      return new Set<string>();
    }
    return new Set(schedules.data.items.map((s) => s.id));
  }, [schedules]);

  const projectActivities = useMemo(() => {
    if (calendarActivities.kind !== "ready") {
      return [];
    }
    return calendarActivities.data.items.filter((activity) =>
      projectScheduleIds.has(activity.scheduleId),
    );
  }, [calendarActivities, projectScheduleIds]);

  const projectRfis = useMemo(() => {
    if (rfis.kind !== "ready") {
      return [];
    }
    return rfis.data.items.filter((rfi) => projectContractIds.has(rfi.contractId));
  }, [rfis, projectContractIds]);

  const projectItps = useMemo(() => {
    if (itps.kind !== "ready") {
      return [];
    }
    return itps.data.items.filter((itp) => projectContractIds.has(itp.contractId));
  }, [itps, projectContractIds]);

  const projectVerifications = useMemo(() => {
    if (verifications.kind !== "ready") {
      return [];
    }
    return verifications.data.items.filter(
      (v) => v.contractId !== null && projectContractIds.has(v.contractId),
    );
  }, [verifications, projectContractIds]);

  const projectFindingsAll = useMemo(() => {
    if (findings.kind !== "ready") {
      return [];
    }
    return findings.data.items;
  }, [findings]);

  const projectFindingsNonPunch = useMemo(() => {
    return projectFindingsAll.filter(
      (f) =>
        f.punchListId === null &&
        f.findingStatus !== "IN_PUNCH_LIST" &&
        f.findingStatus !== "PUNCH_COMPLETED",
    );
  }, [projectFindingsAll]);

  const projectPunches = useMemo(() => {
    return projectFindingsAll.filter(
      (f) =>
        f.punchListId !== null ||
        f.findingStatus === "IN_PUNCH_LIST" ||
        f.findingStatus === "PUNCH_COMPLETED",
    );
  }, [projectFindingsAll]);

  const findingSeverityRows: readonly DivergingRow[] = useMemo(() => {
    return FINDING_SEVERITY_CLASSES.map((code) => {
      const matching = projectFindingsNonPunch.filter((f) => f.findingClass === code);
      const positive = matching.filter((f) => f.findingStatus !== "CLOSED").length;
      const negative = matching.filter((f) => f.findingStatus === "CLOSED").length;
      return {
        label: term(code),
        positive,
        negative,
      };
    });
  }, [projectFindingsNonPunch]);

  const verificationStateRows: readonly BarDatum[] = useMemo(() => {
    let planned = 0;
    let agreed = 0;
    let conducted = 0;
    let awaitingApproval = 0;
    let awaitingClose = 0;
    let closed = 0;

    for (const v of projectVerifications) {
      if (v.workflowState === "CLOSED") {
        closed += 1;
      } else if (v.workflowState === "FINAL_APPROVAL") {
        if (v.resultsApprovedAt !== null) {
          awaitingClose += 1;
        } else {
          awaitingApproval += 1;
        }
      } else if (v.workflowState === "IN_ACTION") {
        conducted += 1;
      } else if (v.workflowState === "APPROVED" || v.workflowState === "DISTRIBUTED") {
        agreed += 1;
      } else if (v.workflowState === "DRAFT" || v.workflowState === "SUBMITTED") {
        planned += 1;
      }
    }

    return [
      { label: strings.portfolio.verificationStatePlanned, value: planned },
      { label: strings.portfolio.verificationStateAgreed, value: agreed },
      { label: strings.portfolio.verificationStateConducted, value: conducted },
      { label: strings.portfolio.verificationStateAwaitingApproval, value: awaitingApproval },
      { label: strings.portfolio.verificationStateAwaitingClose, value: awaitingClose },
      { label: strings.portfolio.verificationStateClosed, value: closed },
    ];
  }, [projectVerifications]);

  const punchItemsRows: readonly DonutDatum[] = useMemo(() => {
    const openCount = projectPunches.filter(
      (p) => p.findingStatus !== "CLOSED" && p.findingStatus !== "PUNCH_COMPLETED",
    ).length;
    const closedCount = projectPunches.filter(
      (p) => p.findingStatus === "CLOSED" || p.findingStatus === "PUNCH_COMPLETED",
    ).length;
    return [
      { label: strings.portfolio.openLabel, value: openCount },
      { label: strings.portfolio.closedLabel, value: closedCount },
    ];
  }, [projectPunches]);

  const rfiStateRows: readonly BarDatum[] = useMemo(() => {
    const openRfis = projectRfis.filter(
      (r) =>
        r.workflowState !== "CLOSED" &&
        r.workflowState !== "CLOSED_WITHOUT_DATE" &&
        r.workflowState !== "CANCELLED",
    );
    const counts = new Map<string, number>();
    for (const r of openRfis) {
      const label = term(r.workflowState) || r.workflowState;
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return [...counts.entries()].map(([label, value]) => ({ label, value }));
  }, [projectRfis]);

  const itpColumns = useMemo(() => projectItpColumns(), []);
  const verificationColumns = useMemo(() => projectVerificationColumns(), []);
  const findingColumns = useMemo(() => projectFindingColumns(), []);
  const punchColumns = useMemo(() => projectPunchColumns(), []);

  return (
    <>
      {toast !== null && (
        <ToastBar
          message={toast.message}
          tone={toast.tone}
          onDone={() => {
            setToast(null);
          }}
        />
      )}
      <Breadcrumb trail={[strings.portfolio.breadcrumbRoot, project?.humanId ?? null]} />
      {projectDetail.kind === "loading" && projects.kind === "loading" && (
        <LoadingLine label={strings.common.loading} />
      )}
      {((projectDetail.kind === "ready" && project === null) ||
        (projects.kind === "ready" && project === null)) && <AbsencePanel />}
      {project !== null && (
        <>
          <div className="q-actions-row" style={{ alignItems: "center" }}>
            <h2 className="q-shell__heading" style={{ margin: 0 }}>
              {project.name}
            </h2>
            <ProjectStatusChip status={project.status} />
            <HistoryButton
              objectType="PROJECT"
              objectId={project.id}
              recordLabel={project.humanId}
            />
          </div>
          {actGone && (
            <p className="q-note" data-testid="project-act-gone">
              {strings.frontDoor.actGone}
            </p>
          )}
          <NextActionRail
            actions={actions}
            handlers={{
              APPROVE_PROJECT: () => {
                setOverlay("approve");
              },
              ASSIGN_QC_ENGINEER: () => {
                setOverlay("assign");
              },
              // Wave W1.5, finding 3b: this control used to navigate to the page it sat
              // on, which did nothing. It opens the attach dialog.
              ATTACH_DOCUMENT: () => {
                setOverlay("attach");
              },
              OPEN_PROJECT_HISTORY: () => {
                document.getElementById("project-history")?.scrollIntoView();
              },
              OPEN_TASK_LIST: showRecordTasks,
            }}
            disabledReasons={{
              OPEN_TASK_LIST: noRecordTasksReason(recordTasks, strings.recordTasks.project),
            }}
          />
          {/* Wave Experience A, item 2: the bible LEADS with the account's pending actions. */}
          <RecordTasksBlock
            state={recordTasks}
            record={taskRecord}
            label={project.humanId}
            kind={strings.recordTasks.project}
            heading={strings.recordTasks.pendingHeading}
            lede={strings.recordTasks.pendingLede}
          />
          {detail?.qaqcEngineerRequired === true && (
            <div className="q-banner" role="status" data-testid="project-staffing-required">
              <p className="q-banner__heading">{strings.portfolio.staffingRequiredHeading}</p>
              <p className="q-thread__body">{strings.portfolio.staffingRequiredBody}</p>
              <p className="q-thread__body" data-testid="project-staffing-act">
                {offered("ASSIGN_QC_ENGINEER")
                  ? strings.portfolio.staffingRequiredAct
                  : strings.portfolio.staffingRequiredNoRight}
              </p>
            </div>
          )}

          {/* Then the quality summary, then the facts and the registers. */}
          <div className="q-section-bands" data-testid="project-quality-band">
            <Section heading={strings.portfolio.qualitySummaryHeading}>
              <div
                data-testid="project-quality-summary"
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <div
                  className="p-4 rounded-lg bg-card border border-border flex flex-col"
                  data-testid="quality-findings-card"
                >
                  <h4 className="text-sm font-semibold mb-3">
                    {strings.portfolio.findingsBySeverity}
                  </h4>
                  <div className="flex-1 min-h-[220px]">
                    <DivergingBars
                      ariaLabel={strings.portfolio.findingsBySeverity}
                      data={findingSeverityRows}
                      positiveSeriesLabel={strings.portfolio.openLabel}
                      negativeSeriesLabel={strings.portfolio.closedLabel}
                      onSelectRow={(side, _row, index) => {
                        const classCode = FINDING_SEVERITY_CLASSES[index] ?? "NEGATIVE_OBSERVATION";
                        const stateParam = side === "positive" ? "OPEN" : "CLOSED";
                        void navigate(
                          `/findings?projectId=${project.id}&class=${classCode}&state=${stateParam}`,
                        );
                      }}
                    />
                  </div>
                  <p
                    className="q-note text-xs mt-2 text-muted-foreground"
                    data-testid="findings-severity-summary"
                  >
                    {projectFindingsNonPunch.length === 0
                      ? strings.portfolio.findingsEmpty
                      : findingSeverityRows
                          .map(
                            (r) =>
                              `${r.label}: ${String(r.positive)} ${strings.portfolio.openLabel.toLowerCase()}, ${String(r.negative)} ${strings.portfolio.closedLabel.toLowerCase()}`,
                          )
                          .join("; ")}
                  </p>
                </div>

                <div
                  className="p-4 rounded-lg bg-card border border-border flex flex-col"
                  data-testid="quality-verifications-card"
                >
                  <h4 className="text-sm font-semibold mb-3">
                    {strings.portfolio.verificationsByState}
                  </h4>
                  <div className="flex-1 min-h-[220px]">
                    <BarChart
                      ariaLabel={strings.portfolio.verificationsByState}
                      data={verificationStateRows}
                      onSelectDatum={(_datum, index) => {
                        const stateTokens = [
                          "DRAFT,SUBMITTED",
                          "APPROVED,DISTRIBUTED",
                          "IN_ACTION",
                          "FINAL_APPROVAL",
                          "FINAL_APPROVAL",
                          "CLOSED",
                        ];
                        const stateToken = stateTokens[index] ?? "CLOSED";
                        void navigate(`/inspections?state=${stateToken}`);
                      }}
                    />
                  </div>
                  <p
                    className="q-note text-xs mt-2 text-muted-foreground"
                    data-testid="verifications-state-summary"
                  >
                    {projectVerifications.length === 0
                      ? strings.portfolio.verificationsEmpty
                      : verificationStateRows
                          .map((r) => `${r.label}: ${String(r.value)}`)
                          .join("; ")}
                  </p>
                </div>

                <div
                  className="p-4 rounded-lg bg-card border border-border flex flex-col"
                  data-testid="quality-punches-card"
                >
                  <h4 className="text-sm font-semibold mb-3">
                    {strings.portfolio.punchItemsSummary}
                  </h4>
                  <div className="flex-1 min-h-[220px]">
                    <DonutChart
                      ariaLabel={strings.portfolio.punchItemsSummary}
                      data={punchItemsRows}
                      centreLabel={strings.portfolio.punchItemsSummary}
                      onSelectDatum={(datum) => {
                        const stateToken =
                          datum.label === strings.portfolio.openLabel ? "IN_PUNCH_LIST" : "CLOSED";
                        void navigate(`/findings?projectId=${project.id}&state=${stateToken}`);
                      }}
                    />
                  </div>
                  <p
                    className="q-note text-xs mt-2 text-muted-foreground"
                    data-testid="punch-items-summary"
                  >
                    {projectPunches.length === 0
                      ? strings.portfolio.punchesEmpty
                      : `${strings.portfolio.openLabel}: ${String(punchItemsRows[0]?.value ?? 0)}, ${strings.portfolio.closedLabel}: ${String(punchItemsRows[1]?.value ?? 0)}`}
                  </p>
                </div>

                <div
                  className="p-4 rounded-lg bg-card border border-border flex flex-col"
                  data-testid="quality-rfis-card"
                >
                  <h4 className="text-sm font-semibold mb-3">{strings.portfolio.rfisByState}</h4>
                  <div className="flex-1 min-h-[220px]">
                    <BarChart
                      ariaLabel={strings.portfolio.rfisByState}
                      data={rfiStateRows}
                      onSelectDatum={() => {
                        void navigate("/rfis");
                      }}
                    />
                  </div>
                  <p
                    className="q-note text-xs mt-2 text-muted-foreground"
                    data-testid="rfis-state-summary"
                  >
                    {rfiStateRows.length === 0
                      ? strings.portfolio.rfisEmpty
                      : rfiStateRows.map((r) => `${r.label}: ${String(r.value)}`).join("; ")}
                  </p>
                </div>
              </div>
            </Section>
          </div>

          <header className="q-project-header mb-6" data-testid="project-bible-header">
            <Facts
              entries={[
                [strings.common.humanId, <span key="id">{project.humanId}</span>],
                [strings.portfolio.createNumberLabel, project.projectNumber],
                [strings.portfolio.columnCategory, project.category ?? ""],
                [strings.portfolio.columnLocation, project.location ?? ""],
                [
                  strings.frontDoor.costCentreLabel.replace(" (optional)", ""),
                  project.costCentre ?? "",
                ],
                [
                  strings.portfolio.columnDates,
                  dateRange(project.startDate, project.endDate ?? project.completionDate),
                ],
                // N05: the description renders under its OWN label, never under Dates.
                ...(detail?.description
                  ? [
                      [
                        strings.portfolio.descriptionLabel,
                        <span key="description" data-testid="project-description">
                          {detail.description}
                        </span>,
                      ] as [string, React.ReactNode],
                    ]
                  : []),
                [
                  strings.portfolio.createDisciplinesLabel,
                  <span key="disciplines" data-testid="project-disciplines">
                    {(taxonomies.kind === "ready" ? taxonomies.data.disciplines : [])
                      .filter(
                        (one) => one.id !== undefined && project.disciplineIds.includes(one.id),
                      )
                      .map((one) => one.label)
                      .join(", ")}
                  </span>,
                ],
                ...(detail !== null
                  ? [
                      [
                        strings.frontDoor.registeredBy,
                        `${detail.registeredByDisplayName ?? strings.frontDoor.someone} ${day(detail.registeredAt ?? null)}`.trim(),
                      ] as [string, React.ReactNode],
                      [
                        strings.frontDoor.approvedBy,
                        detail.approvedAt === null
                          ? strings.frontDoor.notYet
                          : `${detail.approvedByDisplayName ?? strings.frontDoor.someone} ${day(detail.approvedAt)}`,
                      ] as [string, React.ReactNode],
                      [
                        strings.frontDoor.updatedBy,
                        `${detail.updatedByDisplayName ?? strings.frontDoor.someone} ${day(detail.updatedAt ?? null)}`.trim(),
                      ] as [string, React.ReactNode],
                    ]
                  : []),
              ]}
            />
          </header>

          <div className="q-section-bands" data-testid="project-detail-sections">
            <Section heading={strings.portfolio.contractsAndPartiesHeading}>
              {allContracts.kind === "loading" && <LoadingLine label={strings.common.loading} />}
              {allContracts.kind === "ready" && (
                <ProjectContractTree
                  contracts={allContracts.data.items}
                  projectId={project.id}
                  mayRevoke={detail?.canRevokePortalContracts === true}
                  mayAssign={detail?.canAssignPortalContracts === true}
                />
              )}
            </Section>

            <Section heading={strings.portfolio.scheduleAndCalendarHeading}>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <Link
                  to={`/calendar?project=${project.id}`}
                  className="q-button q-button--quiet"
                  data-testid="project-calendar-link"
                >
                  {strings.portfolio.openCalendarForProject}
                </Link>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showShutdown}
                      onChange={(e) => {
                        setShowShutdown(e.target.checked);
                      }}
                    />
                    <span>{strings.schedules.showShutdown}</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showRamadan}
                      onChange={(e) => {
                        setShowRamadan(e.target.checked);
                      }}
                    />
                    <span>{strings.schedules.showRamadan}</span>
                  </label>
                </div>
              </div>
              <div data-testid="project-yeargrid-container" className="overflow-x-auto">
                <YearGrid
                  year={calendarYear}
                  activities={projectActivities.map(asScheduleActivity)}
                  rfis={projectRfis}
                  periods={
                    calendarPeriods.kind === "ready"
                      ? calendarPeriods.data.map((one) => ({ ...one, note: null }))
                      : []
                  }
                  showShutdown={showShutdown}
                  showRamadan={showRamadan}
                />
              </div>
            </Section>

            <Section heading={strings.portfolio.registersHeading}>
              <div
                role="tablist"
                className="flex flex-wrap items-center gap-2 mb-4"
                data-testid="project-registers-tabs"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={registerTab === "itps"}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    registerTab === "itps"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => {
                    setRegisterTab("itps");
                  }}
                  data-testid="project-registers-tab-itps"
                >
                  {strings.portfolio.itpsRegisterHeading} ({projectItps.length})
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={registerTab === "verifications"}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    registerTab === "verifications"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => {
                    setRegisterTab("verifications");
                  }}
                  data-testid="project-registers-tab-verifications"
                >
                  {strings.portfolio.verificationsRegisterHeading} ({projectVerifications.length})
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={registerTab === "findings"}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    registerTab === "findings"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => {
                    setRegisterTab("findings");
                  }}
                  data-testid="project-registers-tab-findings"
                >
                  {strings.portfolio.findingsRegisterHeading} ({projectFindingsNonPunch.length})
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={registerTab === "punches"}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    registerTab === "punches"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => {
                    setRegisterTab("punches");
                  }}
                  data-testid="project-registers-tab-punches"
                >
                  {strings.portfolio.punchesRegisterHeading} ({projectPunches.length})
                </button>
              </div>
              {registerTab === "itps" && (
                <DataTable
                  data={projectItps}
                  columns={itpColumns}
                  caption={strings.portfolio.itpsRegisterHeading}
                  testId="project-itps"
                  getRowId={(row) => row.id}
                />
              )}
              {registerTab === "verifications" && (
                <DataTable
                  data={projectVerifications}
                  columns={verificationColumns}
                  caption={strings.portfolio.verificationsRegisterHeading}
                  testId="project-verifications"
                  getRowId={(row) => row.id}
                />
              )}
              {registerTab === "findings" && (
                <DataTable
                  data={projectFindingsNonPunch}
                  columns={findingColumns}
                  caption={strings.portfolio.findingsRegisterHeading}
                  testId="project-findings"
                  getRowId={(row) => row.id}
                />
              )}
              {registerTab === "punches" && (
                <DataTable
                  data={projectPunches}
                  columns={punchColumns}
                  caption={strings.portfolio.punchesRegisterHeading}
                  testId="project-punches"
                  getRowId={(row) => row.id}
                />
              )}
            </Section>

            {/* The team and the documents are operator-internal, like the history. */}
            <Section heading={strings.portfolio.historyHeading} id="project-history">
              {detail !== null && offered("OPEN_PROJECT_HISTORY") && (
                <TeamBlock
                  team={detail.team}
                  contractLabel={(contractId) =>
                    allContracts.kind === "ready"
                      ? (allContracts.data.items.find((one) => one.id === contractId)?.humanId ??
                        "")
                      : ""
                  }
                />
              )}
              {offered("ATTACH_DOCUMENT") && (
                <DocumentsBlock
                  token={token}
                  target={{ entityType: "PROJECT", entityId: project.id, label: project.humanId }}
                  stamp={stamp}
                />
              )}
              {offered("OPEN_PROJECT_HISTORY") && (
                <ProjectHistoryBlock token={token} projectId={project.id} stamp={stamp} />
              )}
            </Section>
          </div>
          {shown === "attach" && (
            <AttachDocumentsDialog
              token={token}
              target={{ entityType: "PROJECT", entityId: project.id, label: project.humanId }}
              onClose={() => {
                closeOverlay();
                reread();
              }}
              onAttached={(count) => {
                closeOverlay();
                reread();
                setToast({
                  message: strings.frontDoor.attachedToast(count, project.humanId),
                  tone: "info",
                });
              }}
            />
          )}
          {shown === "approve" && detail !== null && (
            <ApprovalDialog
              token={token}
              project={detail}
              onClose={() => {
                closeOverlay();
              }}
              onApproved={(result) => {
                closeOverlay();
                reread();
                setToast({
                  message:
                    result.status === "REGISTERED"
                      ? strings.frontDoor.approvedRegisteredToast
                      : strings.frontDoor.approvedToast,
                  tone: "info",
                });
              }}
            />
          )}
          {shown === "assign" && (
            <AssignDialog
              token={token}
              target={{ projectId: project.id }}
              seed={detail}
              onClose={() => {
                closeOverlay();
              }}
              onAssigned={(outcome) => {
                closeOverlay();
                reread();
                setToast({
                  message: outcome.itpsRequested
                    ? strings.frontDoor.assignedRegisteredToast
                    : strings.frontDoor.assignedToast(outcome.personLabel),
                  tone: "info",
                });
              }}
            />
          )}
        </>
      )}
    </>
  );
}

export function ContractDetailPage(): React.JSX.Element {
  const token = useToken();
  const { contractId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stamp, setStamp] = useState(0);
  const contractDetail = useQuery(
    (signal) => getContract(token, contractId, signal),
    [token, contractId, stamp],
  );
  const contracts = useQuery(() => listContracts(token), [token]);
  const taxonomies = useQuery(() => getTaxonomies(token), [token]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: "info" | "danger" } | null>(null);
  const act = searchParams.get("act");
  // Wave INST ruling 11: the equipment criticality is the operator's procurement
  // surveillance; the counterparty is not offered it (the server decides every row).
  const operatorSide = mayActAs(useViewerSide(), "operator");
  // Wave W2 (Mahmoud's UAT, CTR-18): "View open tasks" means THIS contract's tasks.
  const taskRecord = useMemo<TaskRecord>(
    () => ({ type: "CONTRACT", id: contractId }),
    [contractId],
  );
  const recordTasks = useRecordTasks(token, taskRecord, stamp);
  const detail = contractDetail.kind === "ready" ? contractDetail.data : null;
  const contract =
    detail ??
    (contracts.kind === "ready"
      ? (contracts.data.items.find((one) => one.id === contractId) ?? null)
      : null);

  // No award (amendment 1): the contract enters as an awarded fact, and the rail carries
  // only what the server offers this caller.
  const actions: readonly NextActionOut[] =
    detail !== null
      ? detail.nextActions
      : contract !== null
        ? [{ code: "OPEN_TASK_LIST", method: "GET", href: "/api/tasks" }]
        : [];

  return (
    <>
      {toast !== null && (
        <ToastBar
          message={toast.message}
          tone={toast.tone}
          onDone={() => {
            setToast(null);
          }}
        />
      )}
      <Breadcrumb trail={[strings.portfolio.breadcrumbRoot, contract?.humanId ?? null]} />
      {contractDetail.kind === "loading" && contracts.kind === "loading" && (
        <LoadingLine label={strings.common.loading} />
      )}
      {((contractDetail.kind === "ready" && contract === null) ||
        (contracts.kind === "ready" && contract === null)) && <AbsencePanel />}
      {contract !== null && (
        <>
          <div className="q-actions-row" style={{ alignItems: "center" }}>
            <h2 className="q-shell__heading" style={{ margin: 0 }}>
              {contract.humanId}
            </h2>
            <HistoryButton
              objectType="CONTRACT"
              objectId={contract.id}
              recordLabel={contract.humanId}
            />
          </div>
          <NextActionRail
            actions={actions}
            handlers={{
              ASSIGN_QC_ENGINEER: () => {
                setAssignOpen(true);
              },
              // Wave W1.5 sweep: the same dead control as the project page's (it routed
              // to the contract page it sat on). It opens the attach dialog.
              ATTACH_DOCUMENT: () => {
                setAttachOpen(true);
              },
              OPEN_TASK_LIST: showRecordTasks,
            }}
            disabledReasons={{
              OPEN_TASK_LIST: noRecordTasksReason(recordTasks, strings.recordTasks.contract),
            }}
          />
          {(detail?.contractorPortalUsers ?? contract.contractorPortalUsers) === 0 && (
            <NoPortalUsersNotice party={contract.contractorPartyName} />
          )}
          <RecordTasksBlock
            state={recordTasks}
            record={taskRecord}
            label={contract.humanId}
            kind={strings.recordTasks.contract}
            inPageAct={(task) =>
              (task.kind === "ASSIGN_QC_ENGINEER" || task.kind === "ASSIGN_PERSONNEL") &&
              actions.some((one) => one.code === "ASSIGN_QC_ENGINEER")
                ? () => {
                    setAssignOpen(true);
                  }
                : undefined
            }
          />
          <Section
            heading={
              detail?.isMain === false
                ? detail.parentContractId === null
                  ? strings.portfolio.tpiContract
                  : strings.portfolio.subContract
                : strings.portfolio.mainContractHeading
            }
          >
            <Facts
              entries={[
                [strings.common.humanId, <span key="id">{contract.humanId}</span>],
                [strings.frontDoor.contractTitleLabel, detail?.title ?? ""],
                [strings.portfolio.columnContractor, contract.contractorPartyName],
                [
                  strings.portfolio.columnStatus,
                  <StateChip key="state" state={contract.status} label={contract.statusLabel} />,
                ],
                [strings.portfolio.columnDates, dateRange(contract.startDate, contract.endDate)],
              ]}
            />
            {detail?.scopeDescription && (
              <p className="q-note" data-testid="contract-scope-description">
                {detail.scopeDescription}
              </p>
            )}
          </Section>
          {detail !== null && !detail.isMain && detail.parentContractId !== null && (
            <p className="q-note" data-testid="contract-sub-note">
              {strings.portfolio.subContractNote(
                contracts.kind === "ready"
                  ? (contracts.data.items.find((one) => one.id === detail.parentContractId)
                      ?.humanId ?? strings.portfolio.mainContractHeading.toLowerCase())
                  : strings.portfolio.mainContractHeading.toLowerCase(),
              )}
            </p>
          )}
          <Section heading={strings.portfolio.createContractScopeLabel}>
            <p className="q-note" data-testid="contract-disciplines">
              {(taxonomies.kind === "ready" ? taxonomies.data.disciplines : [])
                .filter((one) => one.id !== undefined && contract.disciplineIds.includes(one.id))
                .map((one) => one.label)
                .join(", ")}
            </p>
          </Section>
          {/* Wave W2 (Mahmoud's UAT): the team was visible only inside the assign
              dialog. The same block as the project page's, operator-internal like it
              (the server answers a counterparty an empty team; ATTACH_DOCUMENT is the
              operator's own signal here, as OPEN_PROJECT_HISTORY is on the project). */}
          {detail !== null && actions.some((one) => one.code === "ATTACH_DOCUMENT") && (
            <TeamBlock
              team={detail.team}
              heading={strings.frontDoor.contractTeamHeading}
              lede={strings.frontDoor.contractTeamLede}
              empty={strings.frontDoor.contractTeamEmpty}
              contractLabel={(one) => (one === contract.id ? contract.humanId : "")}
            />
          )}
          {actions.some((one) => one.code === "ATTACH_DOCUMENT") && (
            <DocumentsBlock
              token={token}
              target={{ entityType: "CONTRACT", entityId: contract.id, label: contract.humanId }}
              stamp={stamp}
            />
          )}
          {(attachOpen ||
            (act === "attach" && actions.some((one) => one.code === "ATTACH_DOCUMENT"))) && (
            <AttachDocumentsDialog
              token={token}
              target={{ entityType: "CONTRACT", entityId: contract.id, label: contract.humanId }}
              onClose={() => {
                setAttachOpen(false);
                if (act === "attach") {
                  const next = new URLSearchParams(searchParams);
                  next.delete("act");
                  setSearchParams(next, { replace: true });
                }
                setStamp((one) => one + 1);
              }}
              onAttached={(count) => {
                setAttachOpen(false);
                if (act === "attach") {
                  const next = new URLSearchParams(searchParams);
                  next.delete("act");
                  setSearchParams(next, { replace: true });
                }
                setStamp((one) => one + 1);
                setToast({
                  message: strings.frontDoor.attachedToast(count, contract.humanId),
                  tone: "info",
                });
              }}
            />
          )}
          {detail !== null && operatorSide && (
            <StaffingPanel
              token={token}
              contractId={contract.id}
              disciplines={(taxonomies.kind === "ready" ? taxonomies.data.disciplines : []).filter(
                (one) => one.id !== undefined && contract.disciplineIds.includes(one.id),
              )}
              onAdded={(message) => {
                setToast({ message, tone: "info" });
              }}
            />
          )}
          {detail !== null && operatorSide && (
            <CriticalityRatingsBlock
              token={token}
              contractId={contract.id}
              projectId={detail.linkedProjectIds[0] ?? null}
              onRated={(message) => {
                setToast({ message, tone: "info" });
              }}
            />
          )}
          {detail?.isMain === true && (
            <SubContractsFyi
              token={token}
              mainContractId={detail.id}
              projectId={detail.linkedProjectIds[0] ?? null}
            />
          )}
          {assignOpen && (
            <AssignDialog
              token={token}
              target={{ contractId: contract.id }}
              seed={null}
              teamProjectId={detail?.linkedProjectIds[0] ?? null}
              onClose={() => {
                setAssignOpen(false);
              }}
              onAssigned={(outcome) => {
                setAssignOpen(false);
                setStamp((one) => one + 1);
                setToast({
                  message: outcome.itpsRequested
                    ? strings.frontDoor.assignedRegisteredToast
                    : strings.frontDoor.assignedToast(outcome.personLabel),
                  tone: "info",
                });
              }}
            />
          )}
        </>
      )}
    </>
  );
}

/**
 * The main-contractor doctrine (Mahmoud, 2026-10-09; wave W0): the operator SEES the
 * main contractor only. Sub-contracts and their holders are an FYI under the main
 * contract, never separate contractors: read-only, no actions.
 */
function SubContractsFyi({
  token,
  mainContractId,
  projectId,
}: {
  readonly token: string;
  readonly mainContractId: string;
  readonly projectId: string | null;
}): React.JSX.Element {
  const onProject = useQuery(
    (signal) => listContracts(token, { projectId, signal }),
    [token, projectId],
  );
  const all = onProject.kind === "ready" ? onProject.data.items : [];
  const byId = new Map(all.map((one) => [one.id, one]));
  // Every contract whose parent chain reaches this main contract. Retired contracts
  // stay out of the FYI: they remain in history, never in the living picture.
  const under = all.filter((one) => {
    if (one.status === "RETIRED") {
      return false;
    }
    let parent = one.parentContractId;
    for (let depth = 0; parent !== null && depth < 32; depth += 1) {
      if (parent === mainContractId) {
        return true;
      }
      parent = byId.get(parent)?.parentContractId ?? null;
    }
    return false;
  });
  return (
    <section className="q-section" data-testid="sub-contracts-fyi">
      <h3 className="q-section__heading">{strings.portfolio.subContractsHeading}</h3>
      <p className="q-note">{strings.portfolio.subContractsLede}</p>
      {onProject.kind === "loading" && <LoadingLine label={strings.common.loading} />}
      {onProject.kind === "ready" && under.length === 0 && (
        <p className="q-note">{strings.portfolio.subContractsEmpty}</p>
      )}
      {under.length > 0 && (
        <div className="q-register-viewport">
          <table className="q-register">
            <caption>{strings.portfolio.subContractsHeading}</caption>
            <thead>
              <tr>
                <th scope="col">{strings.portfolio.columnContract}</th>
                <th scope="col">{strings.portfolio.columnParty}</th>
                <th scope="col">{strings.portfolio.columnStatus}</th>
              </tr>
            </thead>
            <tbody>
              {under.map((one) => (
                <tr key={one.id}>
                  <td>
                    <span className="q-register__mono">{one.humanId}</span>
                    <div className="q-register__wrap">
                      {strings.portfolio.subContractUnder(
                        byId.get(one.parentContractId ?? "")?.humanId ?? "",
                      )}
                    </div>
                  </td>
                  <td>{one.contractorPartyName}</td>
                  <td>
                    <StateChip state={one.status} label={one.statusLabel} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------------- */
/* Parties & Contractors.                                                  */
/* ---------------------------------------------------------------------- */

export function PartiesPage(): React.JSX.Element {
  const token = useToken();
  const dossiers = useQuery(() => listContractorDossiers(token), [token]);
  const columns = useMemo(() => partyRegisterColumns(), []);

  return (
    <>
      <header className="q-ui mb-(--q-s1) flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1 basis-[28rem]">
          <h2 className="q-shell__heading">{strings.portfolio.partiesHeading}</h2>
          <p className="q-shell__lede">{strings.portfolio.partiesLede}</p>
        </div>
      </header>

      <RegisterCard>
        {dossiers.kind === "loading" && <LoadingLine label={strings.common.loading} />}
        {dossiers.kind === "absence" && <AbsencePanel />}
        {dossiers.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
        {dossiers.kind === "ready" &&
          (dossiers.data.length === 0 ? (
            <EmptyState
              title={strings.portfolio.partiesEmptyTitle}
              body={strings.portfolio.partiesEmptyBody}
            />
          ) : (
            <DataTable
              data={dossiers.data}
              columns={columns}
              caption={strings.portfolio.partiesHeading}
              testId="parties"
              getRowId={(row) => row.partyId}
            />
          ))}
      </RegisterCard>
      <p className="q-note q-gap-top">{strings.portfolio.dossierNote}</p>
    </>
  );
}

/** The parties register's columns: what each cell says, in plain words. */
function partyRegisterColumns(): RegisterColumn<ContractorDossier>[] {
  const copy = strings.portfolio;
  return [
    {
      id: "party",
      label: copy.columnParty,
      text: (row) => row.partyName,
      render: (row) => (
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/evaluations?party=${row.partyId}`}
            className="tracking-[0.03em] whitespace-nowrap text-gold no-underline hover:underline"
            data-testid="party-dossier-link"
          >
            {row.partyName}
          </Link>
          <HistoryButton objectType="PARTY" objectId={row.partyId} recordLabel={row.partyName} />
        </div>
      ),
    },
    {
      id: "inspections",
      label: copy.columnInspections,
      text: (row) => String(row.inspectionsConducted),
      sortValue: (row) => row.inspectionsConducted,
    },
    {
      id: "passRate",
      label: copy.columnPassRate,
      text: (row) => (row.firstTimePassRate === null ? "" : percent(row.firstTimePassRate)),
      sortValue: (row) => row.firstTimePassRate ?? -1,
    },
    {
      id: "overdue",
      label: copy.columnOverdue,
      text: (row) => String(row.overdueActions),
      sortValue: (row) => row.overdueActions,
    },
    {
      id: "portalUsers",
      label: copy.columnPortalUsers,
      text: (row) =>
        row.portalUsers === 0
          ? copy.portalUsersNone
          : row.portalUsers === null
            ? ""
            : String(row.portalUsers),
      sortValue: (row) => row.portalUsers ?? -1,
      render: (row) => (
        <div data-testid="party-portal-users">
          {row.portalUsers === 0 ? (
            <>
              <strong>{copy.portalUsersNone}</strong>
              <div className="q-note">{copy.portalUsersNoneNote}</div>
            </>
          ) : row.portalUsers === null ? (
            ""
          ) : (
            String(row.portalUsers)
          )}
        </div>
      ),
    },
  ];
}

/** The read model's rate is already in percentage points; render it as it arrived. */
export function percent(rate: number): string {
  return `${String(Math.round(rate))}%`;
}
